import type { AggregationMethod } from '@src/components/AppLayout/Panels/Intel/AggregatedMetric/AggregatedMetric.types';
import type { SignalsTimeRange } from '@src/components/AppLayout/Panels/Intel/core';
import type {
  FundingRateBlotterView,
  FundingRateUpdateMode,
} from '@src/components/AppLayout/Panels/Intel/FundingRateBlotter/FundingRateBlotter.types';
import type {
  FundingRateFrequency,
  FundingRateMAWindow,
  FundingRateScope,
  FundingRateTimeRange,
} from '@src/components/AppLayout/Panels/Intel/HistoricalFundingRate/HistoricalFundingRate.types';
import type { SpaceParameterPanelValueSupport, SpaceParameterValues } from '@src/components/AppLayout/SpaceParameters';
import type { MultilegComboType } from '@src/components/MultilegCombo/enums';
import type { PreTradeTabType } from '@src/components/PreTrade/types';
import type { PriceGridFilterState } from '@src/components/PriceGrid/types';
import type { PredictionMarketsPriceGridFilterState } from '@src/components/PriceGrid/variants/PredictionMarketsPriceGrid/types';
import type { BalancesV2TabProps } from '@src/containers/Blotters/BalancesV2/types';
import type { CreditTabProps } from '@src/containers/Blotters/Credit/types';
import type { CustomerAssetTransactionsTabProps } from '@src/containers/Blotters/CustomerAssetTransactions/types';
import type { HedgePositionStatusTabProps } from '@src/containers/Blotters/HedgePositionStatus/types';
import type { OrderBlotterDefaultColumn } from '@src/containers/Blotters/Orders/orderColumnIds';
import type { LoanTabProps } from '@src/containers/Blotters/OTC/Loans/Loans';
import type { OTCTabProps } from '@src/containers/Blotters/OTC/OTCDerivatives/OTCDerivatives';
import type { RepoTabProps } from '@src/containers/Blotters/OTC/Repos/Repos';
import type { PositionsTabProps } from '@src/containers/Blotters/PositionsV3/types';
import type { ReconMismatchesTabProps } from '@src/containers/Blotters/Reconciliation/ReconMismatches';
import type { TransfersTabProps } from '@src/containers/Blotters/Transfers';
import type { CustomerBalancesTabsProps } from '@src/containers/Dealer/CustomerBalances/types';
import type { CustomerTransactionsTabsProps } from '@src/containers/Dealer/CustomerTransactions/types';
import type { OrderDetailsPath, OrderRouteType } from '@src/containers/Trading/Markets/OrderDetails/types';
import type {
  BlotterTableFilter,
  BlotterTableState,
  Column,
  CustomerOrder,
  CustomerTrade,
  DeviceType,
  IconName,
  LedgerAccountTypeEnum,
  MarketDataCardConfig,
  MarketDataCardUnifiedLiquidityMarket,
  PredictionMarketCardConfig,
  ResolutionEnumNoPrefix,
  TabProps,
  TabsState,
  Trade,
  WatchlistProps,
} from '@talos/kyoko';
import type { AgAnnotation, AgPriceVolumeChartType } from 'ag-charts-community';

import type { GroupviewPanelState, IDockviewPanel, IDockviewPanelProps, SerializedDockview } from 'dockview';
import type { FunctionComponent } from 'react';
import type { CustomerOrdersTableFilter } from '../containers/Blotters/CustomerOrders/useCustomerOrdersFilter';
import type { CustomerTradeFilter } from '../containers/Blotters/CustomerTrades/useCustomerTradeFilter';
import type { EntityMonitoringPanelType, EntityName } from '../containers/Monitoring/Blotters/types';
import { ENTITY_MONITORING_PANEL_TYPES } from '../containers/Monitoring/Blotters/types';
import type { OptionFilter, StrikeFilter } from '../containers/Trading/Markets/Options/Filter/types';
import type { DealBookingType } from './OTC/otcSharedTypes';

export interface DockviewPanelParams extends IDockviewPanel {
  params: PanelConfig;
}

export interface LayoutOption {
  label: string;
  value: string;
  hasWatchlist?: boolean;
  layoutSpec: LayoutSpec;
}

export interface TabGroupSpec {
  type: string;
  label: string;
  icon?: IconName;
}

export interface TabComponentSpec {
  type: string;
  label: string;
  group: TabGroupSpec;
  renderer: (props: IDockviewPanelProps) => JSX.Element;
}

export interface LayoutConstraint {
  ids: string[];
  groupId: string;
  maximumWidth?: number;
  minimumWidth?: number;
  maximumHeight?: number;
  minimumHeight?: number;
  width?: number;
  height?: number;
  hidden?: boolean;
}

export const VALID_SECTIONS = ['trading', 'monitoring', 'intel', 'dealer'] as const;
export type LayoutSection = (typeof VALID_SECTIONS)[number];
export function isValidLayoutSection(section: string | undefined): section is LayoutSection {
  return section != null && VALID_SECTIONS.includes(section as LayoutSection);
}

export interface SpacesSectionConfig {
  initialized: boolean;
  /** Dockview layout specs for this section; canonical store (replaces top-level appConfig `layoutConfigs[section]`). */
  layouts?: LayoutSpec[];
}

/** Persisted `spacesConfigs` root: layout sections plus optional migration metadata. */
export type AppSpacesConfigsPersistence = Record<LayoutSection, SpacesSectionConfig> & {
  /** Set by migrateSpacesNavFromLayoutConfigs; bump the spaces-nav migration version constant to re-run from preserved `layoutConfigs`. */
  spacesNavLayoutMigrationVersion?: number;
};

/** Tab label in the layout add-panel menu for panels with `group: undefined` (one tab per section when both appear). */
export const LAYOUT_SECTION_DISPLAY_LABEL: Record<LayoutSection, string> = {
  trading: 'Trading',
  monitoring: 'Monitoring',
  intel: 'Intel',
  dealer: 'Dealer',
};

/** How borrowed sections are bucketed in the layout menu. The dockview add-tab menu always uses panel groups. */
export const LayoutMenuGrouping = {
  PanelGroup: 'panelGroup',
  Section: 'section',
} as const;
export type LayoutMenuGrouping = (typeof LayoutMenuGrouping)[keyof typeof LayoutMenuGrouping];

/** Panels from another space that can be added here, and how the layout menu should bucket them. */
export type AdditionalContentSection = {
  section: LayoutSection;
  /**
   * Default `panelGroup`: keep `panel.group` tabs from that space.
   * `section`: one tab named for the section, ignoring those groups.
   */
  layoutMenuGrouping?: LayoutMenuGrouping;
};

export function additionalContentSectionNames(sections: readonly AdditionalContentSection[]): LayoutSection[] {
  return sections.map(section => section.section);
}

export const PageHeaderType = {
  Default: 'default',
  DealerSpaceParameters: 'dealerSpaceParameters',
  TradingDefault: 'tradingDefault',
} as const;
export type PageHeaderType = (typeof PageHeaderType)[keyof typeof PageHeaderType];

export interface LayoutConstraints {
  groupId: string;
  panelIds: string[];
  minWidth?: number;
  maxWidth?: number;
  minHeight?: number;
  maxHeight?: number;
}

export interface CollapsablePanelConfig {
  groupId: string;
  direction: 'left' | 'right';
  defaultSize?: number;
  buttonLabel?: string;
}

export type PanelConfig =
  | AccountLedgerEventsDetailsPanelConfig
  | BalancesBlotterPanelConfig
  | CareOrderManagementPanelConfig
  | CareOrdersPanelConfig
  | CounterpartyMonitoringPanelConfig
  | CreditBlotterPanelConfig
  | DealerSpaceParametersPanelConfig
  | CustomerBalancesBlotterPanelConfig
  | CustomerOrdersBlotterPanelConfig
  | CustomerTradesBlotterPanelConfig
  | CustomerTransfersBlotterPanelConfig
  | CustomerAssetTransactionsBlotterPanelConfig
  | CustomerAssetTransactionsPanelConfig
  | CustomerSecurityMasterPanelConfig
  | CustomerSymbolGroupsPanelConfig
  | CustomerAccountConfigurationsPanelConfig
  | CustomerConfigurationsPanelConfig
  | CustomerAllInTiersPanelConfig
  | CustomerFeeTiersPanelConfig
  | CustomerPricingRulesPanelConfig
  | CustomerSubAccountMappingsPanelConfig
  | CustomerAggregationsPanelConfig
  | CustomerOrderAcceptanceRulesPanelConfig
  | CustomerRFQProtocolRulesPanelConfig
  | CustomerAcquisitionLimitsPanelConfig
  | CustomerAcquisitionLimitMembershipsPanelConfig
  | CustomerUsersPanelConfig
  | CustomerAccountsPanelConfig
  | CustomerFIXConnectionsPanelConfig
  | CustomerAddressesPanelConfig
  | CustomerPositionsPanelConfig
  | CustomerMarginConfigurationPanelConfig
  | CustomerFundingRateConfigurationPanelConfig
  | CustomerScheduleConfigurationPanelConfig
  | CustomerCreditsPanelConfig
  | DeepDivePanelConfig
  | HedgePositionStatusPanelConfig
  | DealCashflowsPanelConfig
  | LoansBlotterPanelConfig
  | MarketCardPanelConfig
  | MultilegPanelConfig
  | OTCDerivativesBlotterPanelConfig
  | OptionsPanelConfig
  | OrderDetailsPanelConfig
  | OrdersBlotterPanelConfig
  | PositionAutoHedgingRulesPanelConfig
  | PositionsBlotterPanelConfig
  | PriceGridV2PanelConfig
  | CustomerPriceGridV2PanelConfig
  | ReconMismatchesPanelConfig
  | ReconMismatchesDetailsPanelConfig
  | ReposBlotterPanelConfig
  | RFQsBlotterPanelConfig
  | TradesBlotterPanelConfig
  | TransfersBlotterPanelConfig
  | WatchlistPanelConfig
  | ManualRFQsPanelConfig
  | HistoricalPriceChartPanelConfig
  | OrderBookPanelConfig
  | MultilegOrderBookPanelConfig
  | TradesHistoryPanelConfig
  | VolumeLadderPanelConfig
  | PriceLadderPanelConfig
  | DealProductBuilderPanelConfig
  | TakerQuoteBreakdownPanelConfig
  | ManualRFQPricingPanelConfig
  | RFQSweepingPanelConfig
  | PreTradePanelConfig
  | MonitoringBlotterPanelConfig
  | EntityOverrideMonitoringPanelConfig
  | SingleAssetMetricPanelConfig
  | AggregatedMetricPanelConfig
  | RelativePerformancePanelConfig
  | HistoricalFundingRatePanelConfig
  | FundingRateBlotterPanelConfig
  | AssetLeaderboardPanelConfig
  | PredictionMarketPanelConfig
  | PredictionMarketsPriceGridPanelConfig
  | DefaultPanelConfig;

export interface DefaultPanelConfig extends BasePanelConfig {
  type:
    | 'ordersBlotter'
    | 'rfqsBlotter'
    | 'tradesBlotter'
    | 'customerOrdersBlotter'
    | 'customerTradesBlotter'
    | 'balancesBlotter'
    | 'customerBalancesBlotter'
    | 'positionsBlotter'
    | 'creditBlotter'
    | 'customerCreditBlotter'
    | 'transfersBlotter'
    | 'customerTransfersBlotter'
    | 'customerAssetTransactionsBlotter'
    | 'marketAccountsBlotter'
    | 'reconMismatches'
    | 'allocations'
    | 'hedgePositionStatus'
    | 'otcDerivatives'
    | 'loans'
    | 'repos';
}

export interface SingleAssetMetricPanelConfig extends BasePanelConfig {
  type: 'marketCapEstimated';
  /** Persisted asset selection (Talos `Currency.Symbol`). Undefined until the user picks one or the asset list resolves. */
  symbol?: string;
  /** Persisted CoinMetrics metric ID (e.g. `'CapMrktEstUSD'`). Undefined falls back to the module default. */
  metric?: string;
  /** Persisted time-range selection. Undefined falls back to `SIGNALS_DEFAULT_TIME_RANGE`. */
  timeRange?: SignalsTimeRange;
}

export interface AggregatedMetricPanelConfig extends BasePanelConfig {
  type: 'aggregatedAssetMetric';
  /** Persisted CoinMetrics metric ID (e.g. `'CapMrktEstUSD'`). Undefined falls back to the module default. */
  metric?: string;
  /** Persisted CoinMetrics asset IDs (e.g. `['btc','eth']`). Undefined falls back to the module defaults. */
  selectedCmAssetIds?: string[];
  /** Persisted aggregation method. Undefined falls back to the module default. */
  aggregationMethod?: AggregationMethod;
  /** Persisted time-range selection. Undefined falls back to `SIGNALS_DEFAULT_TIME_RANGE`. */
  timeRange?: SignalsTimeRange;
  /** Set to `true` when the panel is first added so the config view opens immediately; cleared on mount. */
  settingsInitialOpen?: boolean;
}

export interface RelativePerformancePanelConfig extends BasePanelConfig {
  type: 'relativePerformance';
  /** Persisted CoinMetrics asset IDs (e.g. `['btc','eth']`), max 4. Undefined falls back to the module default. */
  cmAssetIds?: string[];
  /** Persisted CoinMetrics metric ID (e.g. `'PriceUSD'`). Undefined falls back to the module default. */
  metric?: string;
  /** Start of the comparison window ('YYYY-MM-DD'). Undefined defaults to today minus 3 months on first mount. */
  startDate?: string;
  /** End of the comparison window ('YYYY-MM-DD'). Undefined defaults to today on first mount. */
  endDate?: string;
  /**
   * Tracks which quick-set time-range button is currently active.
   * `null` means the user has set a custom date range that doesn't match a preset.
   * Undefined falls back to `RELATIVE_PERFORMANCE_DEFAULT_TIME_RANGE`.
   */
  activeTimeRange?: SignalsTimeRange | null;
  /** When true (default), the chart shows % change from the start-date anchor. When false, raw metric values are shown. */
  isRelative?: boolean;
}

export interface HistoricalFundingRatePanelConfig extends BasePanelConfig {
  type: 'historicalFundingRate';
  /** Persisted CoinMetrics asset ID (e.g. `'btc'`). Undefined until the user picks one. */
  cmAssetId?: string;
  /** Whether to show one line per exchange or a single asset-level aggregate. Undefined falls back to `FUNDING_RATE_DEFAULT_SCOPE`. */
  scope?: FundingRateScope;
  /** Data frequency (hourly or daily). Undefined falls back to `FUNDING_RATE_DEFAULT_FREQUENCY`. */
  frequency?: FundingRateFrequency;
  /** Displayed time window. Undefined falls back to `FUNDING_RATE_DEFAULT_TIME_RANGE`. */
  timeRange?: FundingRateTimeRange;
  /** Moving average window in days (0 = off). Undefined falls back to `FUNDING_RATE_DEFAULT_MA_WINDOW`. */
  maWindow?: FundingRateMAWindow;
  /** Exchanges shown in By Exchange scope. Undefined defaults to the first 5 alphabetically. Max 5. */
  selectedExchanges?: string[];
}

export interface FundingRateBlotterPanelConfig extends BasePanelConfig {
  type: 'fundingRateBlotter';
  /** Configured CoinMetrics asset IDs (e.g. `['btc','eth']`). Undefined falls back to the module default basket. */
  assetList?: string[];
  /** Refresh cadence — Hourly or Daily; live and editable. Undefined falls back to the module default. */
  updateMode?: FundingRateUpdateMode;
  /** View the panel loads in. Undefined falls back to the module default. */
  defaultView?: FundingRateBlotterView;
  /** Set to `true` when the panel is first added so the config view opens immediately; cleared on setup completion (Done), not on mount, so a reload mid-setup resumes the config view. */
  settingsInitialOpen?: boolean;
}

export interface AssetLeaderboardPanelConfig extends BasePanelConfig {
  type: 'assetLeaderboard';
  /** Persisted ag-grid sort/column state, keyed by blotterID (this panel's instance id). */
  blotterTablesState?: Record<string, BlotterTableState>;
}

export type DefaultRoutedPanelTypes =
  | 'careOrderManagement'
  | 'careOrders'
  | 'marketCards'
  | 'predictionMarkets'
  | 'predictionMarketsPriceGrid'
  | 'preTrade'
  | 'priceGridV2'
  | 'multilegPriceGridV2'
  | 'customerPriceGridV2'
  | 'watchlist'
  | 'manualRfqs'
  | 'takerQuoteBreakdown'
  | 'manualRFQPricing'
  | 'rfqSweeping'
  | 'orderBook'
  | 'multilegOrderBook'
  | 'tradesHistory'
  | 'historicalPriceChart'
  | 'priceLadder'
  | 'volumeLadder'
  | 'counterpartyMonitoring'
  | 'monitoringBlotter'
  | 'marketCapEstimated'
  | 'aggregatedAssetMetric'
  | 'relativePerformance'
  | 'historicalFundingRate'
  | 'fundingRateBlotter'
  | 'assetLeaderboard'
  | 'dealerSpaceParameters'
  | 'customerSecurityMaster'
  | 'customerSymbolGroups'
  | 'customerAccountConfigurations'
  | 'customerConfigurations'
  | 'customerAllInTiers'
  | 'customerFeeTiers'
  | 'customerPricingRules'
  | 'customerSubAccountMappings'
  | 'customerAggregations'
  | 'customerOrderAcceptanceRules'
  | 'customerRFQProtocolRules'
  | 'customerAcquisitionLimits'
  | 'customerAcquisitionLimitMemberships'
  | 'customerUsers'
  | 'customerAssetTransactions'
  | 'customerAccounts'
  | 'customerFIXConnections'
  | 'customerAddresses'
  | 'customerPositions'
  | 'customerMarginConfiguration'
  | 'customerFundingRateConfiguration'
  | 'customerScheduleConfiguration'
  | 'customerCredits'
  | EntityMonitoringPanelType
  | DefaultPanelConfig['type'];

export interface CareOrdersPanelConfig extends BasePanelConfig {
  type: 'careOrders';
  tabs: TabsState<TabProps & { showAllDecimals?: boolean; expandAllGroups?: boolean }> | undefined;
  blotterTablesState?: Record<string, BlotterTableState>;
}

export interface CareOrderManagementPanelConfig extends BasePanelConfig {
  type: 'careOrderManagement';
  tabs: TabsState<TabProps> | undefined;
  blotterTablesState?: Record<string, BlotterTableState>;
}
export interface MarketCardTabDefaults {
  defaultSubAccountId: number | undefined;
}

export interface ManualRFQsPanelConfig extends BasePanelConfig {
  type: 'manualRfqs';
  showAllDecimals: boolean | undefined;
}

export interface ManualRFQPricingPanelConfig extends BasePanelConfig {
  type: 'manualRFQPricing';
  rfqID: string | undefined;
}

export interface RFQSweepingPanelConfig extends BasePanelConfig {
  type: 'rfqSweeping';
  sweepGroupID: string | undefined;
}

export interface TakerQuoteBreakdownPanelConfig extends BasePanelConfig {
  type: 'takerQuoteBreakdown';
  rfqID: string | undefined;
}

export interface PositionAutoHedgingRulesPanelConfig extends BasePanelConfig {
  type: 'positionAutoHedgingRules';
}

export interface HistoricalPriceChartPanelConfig extends SingleSymbolPanelConfig {
  type: 'historicalPriceChart';
  chartResolution: ResolutionEnumNoPrefix | undefined;
  annotations: AgAnnotation[] | undefined;
  chartType: AgPriceVolumeChartType | undefined;
}

export interface OrderBookPanelConfig extends SingleSymbolPanelConfig {
  type: 'orderBook';
  showCumQty: boolean | undefined;
  priceIncrement: string | undefined;
}

export interface MultilegOrderBookPanelConfig extends BasePanelConfig {
  type: 'multilegOrderBook';
  symbol: string | undefined;
  compactOrderBook: boolean | undefined;
}

export interface VolumeLadderPanelConfig extends SingleSymbolPanelConfig {
  type: 'volumeLadder';
  sizeBuckets: string[];
  priceIncrement: string | undefined;
  resolution: ResolutionEnumNoPrefix;
}

export interface PriceLadderPanelConfig extends SingleSymbolPanelConfig {
  type: 'priceLadder';
  priceIncrement: string | undefined;
}

export interface TradesHistoryPanelConfig extends SingleSymbolPanelConfig {
  type: 'tradesHistory';
  showCumQty: boolean | undefined;
  priceIncrement: string | undefined;
}

export interface WatchlistPanelConfig
  extends
    Omit<
      WatchlistProps,
      | 'enabledColumns'
      | 'securityTypeOptions'
      | 'setShowOnlyFavorites'
      | 'setSecurityTypes'
      | 'setFilteredCurrencies'
      | 'setFavoriteSecurities'
      | 'favoriteSecurities'
    >,
    BasePanelConfig {
  type: 'watchlist';
}

export interface MarketCardPanelConfig extends BasePanelConfig {
  type: 'marketCards';
  columns: MarketDataCardConfig[][];
  sortCards: boolean;
  forceCompact?: boolean;
  defaults?: Partial<MarketCardTabDefaults>;
}

export interface PredictionMarketPanelConfig extends BasePanelConfig {
  type: 'predictionMarkets';
  columns: PredictionMarketCardConfig[][];
  sortCards: boolean;
}

export interface PredictionMarketsPriceGridPanelConfig extends BasePanelConfig {
  type: 'predictionMarketsPriceGrid';
  filterState?: PredictionMarketsPriceGridFilterState;
}

export interface OptionsPanelConfig extends BasePanelConfig {
  type: 'options';
  optionFilter: OptionFilter;
  strikeFilter?: StrikeFilter;
  columnDefsVisibility?: Record<string, boolean>;
}

export interface DeepDivePanelConfig extends BasePanelConfig {
  type: 'deepDive';
  symbol: string;
  aggregation?: string;
  markets: string[];
  marketAccounts: string[];
  priceIncrement?: string;
  showMarkets?: boolean;
  showCumQty?: boolean;
  chartResolution: ResolutionEnumNoPrefix | undefined;
  annotations: AgAnnotation[] | undefined;
  chartType: AgPriceVolumeChartType | undefined;
}

export interface BlotterTablePanelConfig extends BasePanelConfig {
  // filter: Partial<BlotterTableOrderFilter>;
  // columns?: (keyof Order | Partial<Column>)[];
  tabs: TabsState<TabProps> | undefined;
  blotterTablesState?: Record<string, BlotterTableState>;
}

export interface OrderTabProps extends TabProps {
  defaultFilter?: BlotterTableFilter;
  defaultColumns?: OrderBlotterDefaultColumn[];
}

export interface OrdersBlotterPanelConfig extends BlotterTablePanelConfig {
  type: 'ordersBlotter';
  tabs: TabsState<OrderTabProps> | undefined;
}

export interface TradeTabProps extends TabProps {
  defaultFilter?: BlotterTableFilter;
  defaultColumns?: (keyof Trade | Partial<Column>)[];
  showAllDecimals?: boolean;
}

export interface TradesBlotterPanelConfig extends BlotterTablePanelConfig {
  type: 'tradesBlotter';
  tabs: TabsState<TradeTabProps> | undefined;
}

export interface CustomerOrdersTabProps extends TabProps {
  defaultFilter: CustomerOrdersTableFilter;
  defaultColumns?: (keyof CustomerOrder | Partial<Column>)[];
}

export interface CustomerOrdersBlotterPanelConfig extends BlotterTablePanelConfig {
  type: 'customerOrdersBlotter';
  tabs: TabsState<CustomerOrdersTabProps> | undefined;
}

export interface DealerSpaceParametersPanelConfig extends BasePanelConfig {
  type: 'dealerSpaceParameters';
  spaceParameterValues?: SpaceParameterValues;
}

export interface CustomerSecurityMasterPanelConfig extends BlotterTablePanelConfig {
  type: 'customerSecurityMaster';
}

export interface CustomerSymbolGroupsPanelConfig extends BlotterTablePanelConfig {
  type: 'customerSymbolGroups';
}

export interface CustomerAccountConfigurationsPanelConfig extends BlotterTablePanelConfig {
  type: 'customerAccountConfigurations';
}

export interface CustomerConfigurationsPanelConfig extends BlotterTablePanelConfig {
  type: 'customerConfigurations';
}

export interface CustomerAllInTiersPanelConfig extends BlotterTablePanelConfig {
  type: 'customerAllInTiers';
}

export interface CustomerFeeTiersPanelConfig extends BlotterTablePanelConfig {
  type: 'customerFeeTiers';
}

export interface CustomerPricingRulesPanelConfig extends BlotterTablePanelConfig {
  type: 'customerPricingRules';
}

export interface CustomerSubAccountMappingsPanelConfig extends BlotterTablePanelConfig {
  type: 'customerSubAccountMappings';
}

export interface CustomerAggregationsPanelConfig extends BlotterTablePanelConfig {
  type: 'customerAggregations';
}

export interface CustomerOrderAcceptanceRulesPanelConfig extends BlotterTablePanelConfig {
  type: 'customerOrderAcceptanceRules';
}

export interface CustomerRFQProtocolRulesPanelConfig extends BlotterTablePanelConfig {
  type: 'customerRFQProtocolRules';
}

export interface CustomerAcquisitionLimitsPanelConfig extends BlotterTablePanelConfig {
  type: 'customerAcquisitionLimits';
}

export interface CustomerAcquisitionLimitMembershipsPanelConfig extends BlotterTablePanelConfig {
  type: 'customerAcquisitionLimitMemberships';
}

export interface CustomerUsersPanelConfig extends BlotterTablePanelConfig {
  type: 'customerUsers';
  quickFilterText?: string;
}

export interface CustomerAccountsPanelConfig extends BlotterTablePanelConfig {
  type: 'customerAccounts';
}

export interface CustomerFIXConnectionsPanelConfig extends BlotterTablePanelConfig {
  type: 'customerFIXConnections';
}

export interface CustomerCreditsPanelConfig extends BlotterTablePanelConfig {
  type: 'customerCredits';
}

export interface CustomerAddressesPanelConfig extends BlotterTablePanelConfig {
  type: 'customerAddresses';
}

export interface CustomerPositionsPanelConfig extends BlotterTablePanelConfig {
  type: 'customerPositions';
}

export interface CustomerMarginConfigurationPanelConfig extends BlotterTablePanelConfig {
  type: 'customerMarginConfiguration';
}

export interface CustomerFundingRateConfigurationPanelConfig extends BlotterTablePanelConfig {
  type: 'customerFundingRateConfiguration';
}

export interface CustomerScheduleConfigurationPanelConfig extends BlotterTablePanelConfig {
  type: 'customerScheduleConfiguration';
}

export interface CustomerTradesTabProps extends TabProps {
  defaultFilter: CustomerTradeFilter;
  defaultColumns?: (keyof CustomerTrade | Partial<Column>)[];
}

export interface CustomerTradesBlotterPanelConfig extends BlotterTablePanelConfig {
  type: 'customerTradesBlotter';
  tabs: TabsState<CustomerTradesTabProps> | undefined;
}

export interface BalancesBlotterPanelConfig extends BlotterTablePanelConfig {
  type: 'balancesBlotter';
  tabs: TabsState<BalancesV2TabProps> | undefined;
}

export interface CustomerBalancesBlotterPanelConfig extends BlotterTablePanelConfig {
  type: 'customerBalancesBlotter';
  tabs: TabsState<CustomerBalancesTabsProps> | undefined;
}

export interface PositionsBlotterPanelConfig extends BlotterTablePanelConfig {
  type: 'positionsBlotter';
  tabs: TabsState<PositionsTabProps> | undefined;
}

export interface RFQsBlotterPanelConfig extends BlotterTablePanelConfig {
  type: 'rfqsBlotter';
}

export interface CreditBlotterPanelConfig extends BlotterTablePanelConfig {
  type: 'creditBlotter';
  tabs: TabsState<CreditTabProps> | undefined;
}

export interface TransfersBlotterPanelConfig extends BlotterTablePanelConfig {
  type: 'transfersBlotter';
  tabs: TabsState<TransfersTabProps> | undefined;
}

export interface CustomerTransfersBlotterPanelConfig extends BlotterTablePanelConfig {
  type: 'customerTransfersBlotter';
  tabs: TabsState<CustomerTransactionsTabsProps> | undefined;
}

export interface CustomerAssetTransactionsBlotterPanelConfig extends BlotterTablePanelConfig {
  type: 'customerAssetTransactionsBlotter';
  tabs: TabsState<CustomerAssetTransactionsTabProps> | undefined;
}

export interface CustomerAssetTransactionsPanelConfig extends BlotterTablePanelConfig {
  type: 'customerAssetTransactions';
}

export interface OTCDerivativesBlotterPanelConfig extends BlotterTablePanelConfig {
  type: 'otcDerivatives';
  tabs: TabsState<OTCTabProps> | undefined;
}

export interface LoansBlotterPanelConfig extends BlotterTablePanelConfig {
  type: 'loans';
  tabs: TabsState<LoanTabProps> | undefined;
}

export interface ReposBlotterPanelConfig extends BlotterTablePanelConfig {
  type: 'repos';
  tabs: TabsState<RepoTabProps> | undefined;
}

export interface ReconMismatchesPanelConfig extends BlotterTablePanelConfig {
  type: 'reconMismatches';
  tabs: TabsState<ReconMismatchesTabProps> | undefined;
}

export interface HedgePositionStatusPanelConfig extends BlotterTablePanelConfig {
  type: 'hedgePositionStatus';
  tabs: TabsState<HedgePositionStatusTabProps> | undefined;
}

export interface AccountLedgerEventsDetailsPanelConfig extends BasePanelConfig {
  type: 'accountLedgerEvents';
  accountType: LedgerAccountTypeEnum;
  account: string;
  asset?: string;
}

export interface ReconMismatchesDetailsPanelConfig extends BasePanelConfig {
  type: 'reconMismatchesDetails';
  mismatchIDs: string[];
}

export interface OrderDetailsPanelConfig extends BasePanelConfig {
  type: 'orderDetails';
  orderID: string;
  openTab: OrderDetailsPath;
  orderType: OrderRouteType;
}

export interface DealCashflowsPanelConfig extends BasePanelConfig {
  type: 'dealCashflows';
  dealID: string;
}

export interface MultilegPanelConfig extends BasePanelConfig {
  type: 'multilegDeepDive';
  instrumentType: MultilegComboType;
  resolvedSymbol: string | undefined;
  initiatingLegs?: boolean[];
  haircutRatios?: string[];
  compactOrderBook?: boolean;
  legSymbols?: string[];
  legMarketAccounts?: string[];
  legMarkets?: string[];
}
export interface PriceGridV2PanelConfig extends BasePanelConfig {
  type: 'priceGridV2' | 'multilegPriceGridV2';
  filterState?: PriceGridFilterState;
}

export interface CustomerPriceGridV2PanelConfig extends BasePanelConfig {
  type: 'customerPriceGridV2';
  customerID?: string;
  filterState?: PriceGridFilterState;
  marketAccount?: string;
}

export interface MonitoringBlotterPanelConfig extends BasePanelConfig {
  type: 'monitoringBlotter';
  entity: EntityName | undefined;
}

export interface CounterpartyMonitoringPanelConfig extends BasePanelConfig {
  type: 'counterpartyMonitoring';
  tabs: TabsState<TabProps> | undefined;
  blotterTablesState?: Record<string, BlotterTableState>;
}

export interface EntityOverrideMonitoringPanelConfig extends BasePanelConfig {
  type: EntityMonitoringPanelType;
}

export interface BasePanelConfig {
  type: PanelType;
  icon?: string;
  // Timestamp set when triggering an update to the panel
  refresh?: number;
  customTitle?: boolean;
  closable?: boolean;
  editable?: boolean;
}

export interface SingleSymbolPanelConfig extends BasePanelConfig {
  symbol: string | undefined;
  markets: string[] | undefined;
  marketAccounts: string[] | undefined;
  aggregation: string | undefined;
  enableUnifiedLiquidity: boolean | undefined;
  unifiedLiquidityMarketSymbols?: MarketDataCardUnifiedLiquidityMarket[];
  settingsInitialOpen?: boolean; // Whether to open the settings panel by default
  orderPresetID?: string;
  subAccount?: string;
}

export interface LayoutSpec {
  id: string;
  /** The id of the template used to create the layout */
  templateId?: string;
  /** Whether the layout can be removed from the section, undefined means it can be removed */
  isRemovable?: boolean;
  /** The device type the layout is optimized for, undefined is legacy behavior means it is optimized for all device types */
  deviceType?: DeviceType;
  label: string;
  pageHeader?: PageHeaderType;
  dockViewLayout: SerializedDockview;
  /** Values controlled by the static space-parameters header for layouts whose panels support parameterization. */
  spaceParameterValues?: SpaceParameterValues;
  constraints: LayoutConstraints[];
  collapsablePanels: CollapsablePanelConfig[];
  /** Sidebar space group label in app config; omitted from shared templates (`layoutSpecForSharing`). */
  group?: string;
}

export interface TypedGroupviewPanelState
  extends
    Omit<GroupviewPanelState, 'contentComponent'>,
    Record<keyof Pick<GroupviewPanelState, 'contentComponent'>, PanelType> {}

export interface TypedGroupviewPanelConfigState extends TypedGroupviewPanelState {
  params: PanelConfig;
}

export const PANEL_TYPES = [
  'allocations',
  'marketCards',
  'predictionMarkets',
  'predictionMarketsPriceGrid',
  'options',
  'priceGridV2',
  'deepDive',
  'orderDetails',
  'watchlist',
  'ordersBlotter',
  'rfqsBlotter',
  'tradesBlotter',
  'dealerSpaceParameters',
  'customerOrdersBlotter',
  'customerTradesBlotter',
  'balancesBlotter',
  'customerBalancesBlotter',
  'positionsBlotter',
  'creditBlotter',
  'customerCreditBlotter',
  'transfersBlotter',
  'customerTransfersBlotter',
  'customerAssetTransactionsBlotter',
  'customerAssetTransactions',
  'customerSecurityMaster',
  'customerSymbolGroups',
  'customerAccountConfigurations',
  'customerConfigurations',
  'customerAllInTiers',
  'customerFeeTiers',
  'customerPricingRules',
  'customerSubAccountMappings',
  'customerAggregations',
  'customerOrderAcceptanceRules',
  'customerRFQProtocolRules',
  'customerAcquisitionLimits',
  'customerAcquisitionLimitMemberships',
  'customerUsers',
  'customerAccounts',
  'customerFIXConnections',
  'customerAddresses',
  'customerPositions',
  'customerMarginConfiguration',
  'customerFundingRateConfiguration',
  'customerScheduleConfiguration',
  'customerCredits',
  'marketAccountsBlotter',
  'hedgePositionStatus',
  'dealCashflows',
  'positionAutoHedgingRules',
  'reconMismatches',
  'reconMismatchesDetails',
  'multilegDeepDive',
  'accountLedgerEvents',
  'multilegPriceGridV2',
  'customerPriceGridV2',
  'careOrderManagement',
  'careOrders',
  'manualRfqs',
  'historicalPriceChart',
  'orderBook',
  'tradesHistory',
  'multilegOrderBook',
  'volumeLadder',
  'priceLadder',
  'otcDerivatives',
  'loans',
  'repos',
  'dealProductBuilder',
  'takerQuoteBreakdown',
  'manualRFQPricing',
  'rfqSweeping',
  'preTrade',
  'counterpartyMonitoring',
  'monitoringBlotter',
  'marketCapEstimated',
  'aggregatedAssetMetric',
  'relativePerformance',
  'historicalFundingRate',
  'fundingRateBlotter',
  'assetLeaderboard',
  ...ENTITY_MONITORING_PANEL_TYPES,
] as const;
export type PanelType = (typeof PANEL_TYPES)[number];

export interface PanelComponentSpec {
  type: PanelType;
  /** The main product section the panel belongs to */
  section: LayoutSection;
  /** Space parameters and panel filter bindings supported by this panel. */
  spaceParameterSupport?: SpaceParameterPanelValueSupport;
  label: string;
  /** Used for grouping panels in the add panel menu. Undefined means the panel is not grouped. */
  group: TabGroupSpec | undefined;
  renderer?: FunctionComponent<IDockviewPanelProps>;
  canBeAdded?: boolean;
  icon: IconName | undefined;
  initialWidth?: number;
  initialHeight?: number;
  minWidth?: number;
  minHeight?: number;
}

export interface DealProductBuilderPanelConfig extends BasePanelConfig {
  type: 'dealProductBuilder';
  bookingType: DealBookingType;
  resolvedSymbol?: string;
}

export interface PreTradePanelConfig extends BasePanelConfig {
  type: 'preTrade';
  sessionId: string;
  openTab?: PreTradeTabType;
  requestId?: string;
}
